import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useTransition } from '@react-spring/web';
import useLocalStorage from '../../hooks/useLocalStorage';
import useKeyPress from '../../hooks/useKeyPress';
import { checkInTeams } from '../../utils/teams';
import {
  ComponentWrapper,
  VerticalTabWrapper,
  Tab,
  TabText,
  VerticalTabContent,
  Close,
  Header,
  SubHeader,
  Collection,
  CollectionItem,
  Footer,
  Mask,
} from './styles';

const VerticalTab = ({
  id,
  order,
  texts,
  defaultOpen = false,
  forceOpen = false,
  TabComponent = Tab,
  WrapperComponent = VerticalTabWrapper,
  openClassName = 'vertical-tab-open',
  context = {},
  content,
  onOpen,
  onClose,
  disabledByUser,
  localStorageKey = '',
}) => {
  const { header = '', tabheader = '', subheader = '', footer = '', items = [] } = texts || {};
  const inTeams = checkInTeams();

  const [collapsed, setCollapsed] =
    !disabledByUser && !inTeams && !forceOpen && localStorageKey
      ? useLocalStorage(localStorageKey, !defaultOpen)
      : useState(inTeams || disabledByUser ? !forceOpen : !forceOpen || !defaultOpen);

  const showMask = !collapsed && ['vertical-tab-open'].includes(openClassName);
  const body = document.querySelector('body');
  const hasScrollbar = window.innerWidth > document.documentElement.clientWidth || body.classList.contains('horizon');
  const shouldHide = window.innerWidth < 768; // Define small devices

  const setGlobalStateClass = (collapsed) => {
    if (!collapsed) {
      document.querySelector('body').classList.add(openClassName);
    } else {
      document.querySelector('body').classList.remove(openClassName);
    }
  };

  const onClick = () => {
    setCollapsed(!collapsed);
    onClose && onClose();
  };

  const collectionItems = Object.values(items)
    .filter((item) => {
      if (typeof item === 'string') {
        return true;
      } else {
        return !!context[Object.keys(item)[0]];
      }
    })
    .map((item, index) => (
      <CollectionItem
        key={index}
        dangerouslySetInnerHTML={{
          __html: typeof item === 'string' ? item : Object.values(item)[0],
        }}
      />
    ));

  const contentPanelTransitions = useTransition(!collapsed, {
    from: { transform: 'translateX(100%)' },
    enter: { transform: 'translateX(0%)' },
    leave: { transform: 'translateX(100%)' },
  });

  const tabTransitions = useTransition(collapsed, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const maskTransitions = useTransition(showMask, {
    onStart: () => {
      if (!collapsed && showMask && hasScrollbar) {
        body.classList.add('scrollbar');
      }
    },
    from: { opacity: 0 },
    enter: { opacity: 0.7 },
    leave: { opacity: 0 },
    onRest: () => {
      if (collapsed) {
        body.classList.remove('scrollbar');
      }
    },
  });

  const checkScrollPosition = () => {
    const element = document.getElementById(id);
    if (!element) {
      return;
    }

    element.classList.remove('top');
    element.classList.remove('bottom');

    if (element.scrollTop === 0) {
      element.classList.add('top');
    }

    if (Math.round(element.scrollHeight - element.scrollTop) === element.clientHeight) {
      element.classList.add('bottom');
    }
  };

  useEffect(() => {
    const element = document.getElementById(id);
    if (element) {
      checkScrollPosition();
      element.addEventListener('scroll', checkScrollPosition);
      return () => {
        element.removeEventListener('scroll', checkScrollPosition);
      };
    }
  });

  useEffect(() => {
    if (onOpen && !collapsed) {
      onOpen();
    }
  }, [collapsed]);

  useEffect(() => {
    if (content && forceOpen) {
      setCollapsed(false);
    }
  }, [forceOpen]);

  useKeyPress(
    'Escape',
    () => {
      setCollapsed(true);
      onClose && onClose();
    },
    !collapsed,
  );

  setGlobalStateClass(collapsed);

  const verticalTab = shouldHide ? null : (
    <ComponentWrapper className="vertical-tab">
      {tabTransitions(
        (style, show) =>
          show && (
            <TabComponent
              title={tabheader}
              style={style}
              onClick={onClick}
              onKeyPress={onClick}
              tabIndex={0}
              order={order}
              data-testid="vertical-tab"
            >
              <TabText>{tabheader}</TabText>
            </TabComponent>
          ),
      )}
      {contentPanelTransitions(
        (style, show) =>
          show && (
            <WrapperComponent id={id} style={style}>
              <Close
                role="button"
                title={i18n.__('app_close')}
                aria-label={i18n.__('app_close')}
                onClick={onClick}
                onKeyPress={onClick}
                tabIndex={0}
                order={order}
              />

              <VerticalTabContent>
                {header && <Header>{header}</Header>}
                {subheader && <SubHeader>{subheader}</SubHeader>}
                <Collection>
                  {content || (
                    <>
                      {collectionItems}
                      <Footer dangerouslySetInnerHTML={{ __html: footer }} />
                    </>
                  )}
                </Collection>
              </VerticalTabContent>
            </WrapperComponent>
          ),
      )}
      {maskTransitions((style, show) => show && <Mask style={style} />)}
    </ComponentWrapper>
  );

  return ReactDOM.createPortal(verticalTab, document.querySelector('[role="main"]') || document.querySelector('body'));
};

VerticalTab.propTypes = {
  id: PropTypes.string.isRequired,
  order: PropTypes.number.isRequired,
  defaultOpen: PropTypes.bool,
  forceOpen: PropTypes.bool,
  texts: PropTypes.shape({
    header: PropTypes.string,
    subheader: PropTypes.string,
    footer: PropTypes.string,
    tabheader: PropTypes.string,
    items: PropTypes.array,
  }),
  context: PropTypes.shape(),
  content: PropTypes.object,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  disabledByUser: PropTypes.bool,
  TabComponent: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  WrapperComponent: PropTypes.object,
  openClassName: PropTypes.string,
};

export default VerticalTab;
